import { type IPageRouteItem, type TMenuType } from "@/types/types";
import React, { useEffect, useState } from "react";
import { createBrowserRouter, RouterProvider } from "react-router";
import { AppRouter, AppRoute } from "@ice/stark";
import { AppRouteProvider, useAppRouteContext } from "@/contexts";
import { getMenus } from "@/services";
import { isLowcodePage, traverseMenuTree } from "@/utils";
import Layout from "@/layout/main";
import {
  IframePage,
  ProcodePage,
  MaterialPage,
  ErrorPage,
  LowcodePage,
  FormResultPage,
  FormEditPage,
  FormDetailPage,
  OnePageContainer,
} from "@/pages";

// JUST FOR TEST, 线上暂不使用
// import OneSearch from "@/pages/one-search";

// 主应用内部管理的页面路由, 包含低代码页面和 iframe 页面
const FrameworkApp = ({
  pageRouteList = [],
}: {
  pageRouteList: IPageRouteItem[];
}) => {
  const routerJson = [
    // 这里补充一个首页空路由, 是为了避免数据加载过程中命中 errorpage, 造成视觉闪烁
    // 为什么要在pageRouteList有内容的时候才补充, 因为如果pageRouteList最终仍然为空, 则说明没有加载到任何路由, 则需要兜底到 404 页面
    ...(pageRouteList?.length > 0
      ? [
          {
            index: true,
            element: null,
          },
        ]
      : []),

    // 动态路由页面
    {
      path: "/",
      element: <OnePageContainer />, // 父包裹, 实现OnePage模式
      children: [
        ...pageRouteList
          .map(({ type, pageType, path }) => {
            // 低代码页面路由
            if (isLowcodePage(type as TMenuType)) {
              return {
                path,
                children: [
                  {
                    index: true,
                    path: "*?", // 通配符, 用于匹配后续的onePage页面, 目前只在lowcode页面开放该模式
                    element: <LowcodePage />,
                    handle: { pageType: "lowcode" },
                  },
                  ...(pageType === "CREATE"
                    ? [
                        {
                          path: "result",
                          element: <FormResultPage />,
                        },
                        {
                          path: "update",
                          element: <FormEditPage />,
                        },
                        {
                          path: "detail",
                          element: <FormDetailPage />,
                        },
                      ]
                    : []),
                ],
              };
            }
            // iframe 页面路由
            if (type === "iframe") {
              return {
                path,
                element: <IframePage />,
              };
            }
            // procode 页面路由
            if (type === "procode") {
              return {
                path,
                element: <ProcodePage />,
              };
            }
            // react 页面路由
            if (type === "react") {
              return {
                path,
                element: <MaterialPage />,
              };
            }
            // 其他类型不生成路由
            return null;
          })
          .filter(Boolean), // 过滤掉 null 值
      ],
    },

    // JUST FOR TEST, 线上暂不使用
    // {
    //   path: "onesearchdemo/:datasetId",
    //   element: <OneSearch />,
    // },

    // 兜底页面, 处理 404, 空应用等情况
    {
      path: "*",
      element: (
        <ErrorPage errorType={pageRouteList.length > 0 ? "404" : "empty"} />
      ),
    },
  ];

  const routerConfig = createBrowserRouter(routerJson);

  return <RouterProvider router={routerConfig} />;
};

const FrameworkAppMemo = React.memo(FrameworkApp);

const AppContent = () => {
  const [isInit, setIsInit] = useState<boolean>(true); // 是否正在初始化状态, 用于骨架屏渲染
  const [pageRouteList, setPageRouteList] = useState<IPageRouteItem[]>([]); // 主应用内部管理的页面路由
  const [microAppList, setMicroAppList] = useState([]); // 微应用列表
  const [siteInfo, setSiteInfo] = useState({}); // 站点信息
  const { setCurrentPath } = useAppRouteContext();

  useEffect(() => {
    const asyncInitApp = async () => {
      setIsInit(true); // 初始化
      const { menus, siteInfo: site } = await getMenus();

      const pageList: IPageRouteItem[] = []; // 处理需要注册路由的页面列表
      // const subApps = []; // 处理微应用列表
      traverseMenuTree(menus, (node) => {
        // 过滤出所有lowcode和iframe类型的页面菜单
        if (node.key && (isLowcodePage(node.type) || node.type === "iframe")) {
          pageList.push({
            path: node.key,
            type: node.type,
            pageType: node.pageType,
          });
        }

        // 过滤出所有procode类型的页面菜单
        // TODO: 目前通过主应用路由处理procode页面
        if (node.key && node.type === "procode" && node.extend) {
          pageList.push({
            path: node.key,
            type: node.type,
            pageType: node.pageType,
          });
        }

        // 过滤出所有react类型的页面菜单
        if (node.key && node.type === "react") {
          pageList.push({
            path: node.key,
            type: node.type,
            pageType: node.pageType,
          });
        }

        // 过滤出所有subApp类型的页面菜单
        // if (node.key && node.type === "subApp" && node.subAppName) {
        //   const subApp = subApps.find((item) => item.name === node.subAppName);
        //   subApp.activePath = subApp.activePath || [];
        //   if (subApp.activePath) {
        //     // 统一转数组
        //     subApp.activePath =
        //       typeof subApp.activePath === "string"
        //         ? [subApp.activePath]
        //         : subApp.activePath;
        //     // 追加菜单中指定的path
        //     node.key && subApp.activePath.push(node.key);
        //   }
        // }
      });
      setSiteInfo(site);
      setPageRouteList(pageList);
      // setMicroAppList(subApps);
      setIsInit(false); // 初始化
    };

    asyncInitApp();
  }, []);

  return (
    <Layout siteInfo={siteInfo} isInit={isInit}>
      <AppRouter
        onRouteChange={(pathname) => {
          // 更新当前路径，供子组件使用
          setCurrentPath(pathname);
          // TODO: 派发全局自定义事件, 供其他应用监听. 目前router change之后低码renderer组件会重新渲染, 不需要再派发事件了
          // const event = new CustomEvent("APPPLATFORM:app-route-change", {
          //   detail: { pathname },
          // });
          // window.dispatchEvent(event);
        }}
        shouldAssetsRemove={(url, element) => {
          // 主子应用切换时判断哪些资源需要移除
          if (
            url &&
            (url === element?.getAttribute("src") ||
              url === element?.getAttribute("href"))
          )
            return true;
          if (element?.getAttribute("data-micro-app-name")) return true;
          return false;
        }}
      >
        {/* 微应用路由 */}
        {microAppList.map(
          (
            {
              name,
              activePath,
              title,
              url,
              loadScriptMode = "script",
              basename,
            },
            index,
          ) => {
            return (
              <AppRoute
                key={`${name}-${index}`}
                name={name}
                basename={basename}
                activePath={activePath}
                title={title}
                loadScriptMode={loadScriptMode}
                url={url}
                scriptAttributes={[`data-micro-app-name=${name}`]} // 微应用加载的script统一打上data-micro-app-name属性, 用于标记清除
              />
            );
          },
        )}

        {/* 主应用路由, 兜底的 404 等页面都在主应用里面处理 */}
        <AppRoute
          activePath="*"
          render={() => <FrameworkAppMemo pageRouteList={pageRouteList} />}
        />
      </AppRouter>
    </Layout>
  );
};

const App = () => {
  return (
    <AppRouteProvider>
      <AppContent />
    </AppRouteProvider>
  );
};

export default App;
